import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Send, PhoneCall } from "lucide-react";
import { FaInstagram, FaFacebookF } from "react-icons/fa";
import { Reveal, SectionTitle } from "@/components/site/Reveal";
import { Captcha } from "@/components/site/Captcha";
import { CONTACT } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inputClass = "w-full rounded-2xl border border-beige bg-cream px-4 py-3 text-chocolate placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-strawberry/50 focus:border-strawberry transition";

export const ContactSection = () => {
  const [form, setForm] = useState({ nom: "", telephone: "", email: "", message: "", website: "" });
  const [loading, setLoading] = useState(false);
  const [captchaOk, setCaptchaOk] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e, type = "contact") => {
    e.preventDefault();
    if (!form.nom || !form.telephone || !form.email) {
      toast.error("Merci de renseigner vos nom, téléphone et email.");
      return;
    }
    if (!captchaOk) {
      toast.error("Merci de valider le calcul anti-robot.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/quote`, { ...form, type });
      toast.success(type === "rappel" ? "Nous vous rappellerons très vite ! 📞" : "Message envoyé, merci !");
      setForm({ nom: "", telephone: "", email: "", message: "", website: "" });
    } catch {
      toast.error("Une erreur est survenue. Merci de réessayer.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="py-20 sm:py-28 px-5 sm:px-8 bg-cream">
      <div className="max-w-7xl mx-auto">
        <Reveal>
          <SectionTitle overline="Contact" title="Parlons de votre événement" subtitle={`Nous intervenons à ${CONTACT.zone}.`} />
        </Reveal>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <Reveal delay={0.05}>
            <div className="space-y-4">
              <a data-testid="contact-phone" href={`tel:${CONTACT.phoneRaw}`} className="flex items-center gap-4 rounded-3xl bg-white border border-beige p-5 shadow-soft hover:shadow-hover transition">
                <div className="h-12 w-12 rounded-2xl bg-pastelbg flex items-center justify-center"><Phone className="text-strawberry" /></div>
                <div><p className="text-sm text-cocoa">Téléphone</p><p className="font-semibold text-chocolate">{CONTACT.phone}</p></div>
              </a>
              <a data-testid="contact-email" href={`mailto:${CONTACT.email}`} className="flex items-center gap-4 rounded-3xl bg-white border border-beige p-5 shadow-soft hover:shadow-hover transition">
                <div className="h-12 w-12 rounded-2xl bg-pastelbg flex items-center justify-center"><Mail className="text-strawberry" /></div>
                <div><p className="text-sm text-cocoa">Email</p><p className="font-semibold text-chocolate">{CONTACT.email}</p></div>
              </a>
              <div className="flex items-center gap-4 rounded-3xl bg-white border border-beige p-5 shadow-soft">
                <div className="h-12 w-12 rounded-2xl bg-pastelbg flex items-center justify-center"><MapPin className="text-strawberry" /></div>
                <div><p className="text-sm text-cocoa">Zone d'intervention</p><p className="font-semibold text-chocolate">{CONTACT.zone}</p></div>
              </div>

              <div className="flex flex-wrap gap-3 pt-1">
                <a data-testid="contact-instagram" href={CONTACT.instagram} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-full bg-white border border-beige px-5 py-3 font-semibold text-chocolate hover:bg-pastelbg transition">
                  <FaInstagram size={20} className="text-strawberry" /> Instagram
                </a>
                <a data-testid="contact-facebook" href={CONTACT.facebook} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-full bg-white border border-beige px-5 py-3 font-semibold text-chocolate hover:bg-pastelbg transition">
                  <FaFacebookF size={18} className="text-strawberry" /> Facebook
                </a>
              </div>

              <div className="rounded-3xl overflow-hidden border border-beige shadow-soft h-64">
                <iframe
                  title="Carte zone d'intervention Moum's"
                  data-testid="contact-map"
                  src={`https://www.google.com/maps?q=${encodeURIComponent(CONTACT.mapsQuery)}&output=embed`}
                  className="w-full h-full border-0"
                  loading="lazy"
                />
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.12}>
            <form onSubmit={(e) => submit(e, "contact")} data-testid="contact-form" className="rounded-[2rem] bg-white p-6 sm:p-8 shadow-soft border border-beige space-y-4">
              <input type="text" name="website" tabIndex={-1} autoComplete="off" value={form.website} onChange={set("website")} className="absolute -left-[9999px] w-px h-px opacity-0" aria-hidden="true" />
              <h3 className="font-heading text-2xl font-semibold text-chocolate">Envoyez-nous un message</h3>
              <input data-testid="contact-nom" className={inputClass} placeholder="Nom *" value={form.nom} onChange={set("nom")} />
              <input data-testid="contact-tel" className={inputClass} placeholder="Téléphone *" value={form.telephone} onChange={set("telephone")} />
              <input data-testid="contact-mail" type="email" className={inputClass} placeholder="Email *" value={form.email} onChange={set("email")} />
              <textarea data-testid="contact-message" rows={4} className={inputClass} placeholder="Votre message" value={form.message} onChange={set("message")} />
              <Captcha onChange={setCaptchaOk} testid="contact-captcha" />
              <div className="flex flex-col sm:flex-row gap-3">
                <button data-testid="contact-submit" type="submit" disabled={loading} className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-strawberry px-6 py-3.5 font-semibold text-white hover:bg-[#B83044] transition disabled:opacity-60">
                  <Send size={18} /> Envoyer
                </button>
                <button data-testid="contact-rappel" type="button" disabled={loading} onClick={(e) => submit(e, "rappel")} className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-white border border-strawberry px-6 py-3.5 font-semibold text-strawberry hover:bg-pastelbg transition disabled:opacity-60">
                  <PhoneCall size={18} /> Être rappelé
                </button>
              </div>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
